import type {
  SalaryCurrency,
  SalaryFrequency,
} from '@seek/salary-data/lib-types/types';

import { getMinMaxAndAllowables } from 'src/app/v1/views/components/Pay/util/getMinMaxAndAllowables';
import {
  getValidationTranslations,
  type Translator,
} from 'src/app/v1/views/components/Pay/util/getValidationTranslations';

import { deduceMaxSalaryTextError } from './deduceMaxSalaryTextError';
import { getErrorForMaxValidationType } from './getErrorForMaxValidationType';
import { getErrorForMinValidationType } from './getErrorForMinValidationType';
import { minMaxErrorPredicate } from './minMaxErrorPredicate';
import type { SalaryError } from './types';

const getMinMaxError = (minMaxErrMessage: string, predicate: () => boolean) =>
  predicate() ? minMaxErrMessage : null;

interface IStateSetters {
  setSalaryErrorMin: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setSalaryErrorMax: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setSalaryError: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
}

type SalaryDataValidatorInput = {
  minSalary?: number | null;
  maxSalary?: number | null;
  maxSalaryText: string;
  validationType?: 'min' | 'max';
  value?: string;
  skipIfNull?: boolean;
};

export const makeSalaryDataValidator =
  (
    t: Translator,
    currency: SalaryCurrency,
    frequency: SalaryFrequency,
    { setSalaryErrorMin, setSalaryErrorMax, setSalaryError }: IStateSetters,
  ) =>
  ({
    minSalary,
    maxSalary,
    maxSalaryText,
    validationType,
    value,
    skipIfNull,
  }: SalaryDataValidatorInput) => {
    if (skipIfNull) {
      return null;
    }

    let error: SalaryError = {};
    const minMaxAndAllowables = getMinMaxAndAllowables({
      minSalary,
      maxSalary,
      validationType,
      currency,
      frequency,
      value,
    });

    const {
      minimum,
      minimumText,
      maximumText,
      maximum,
      allowableMax,
      allowableMin,
      minAllowableSalary,
      maxAllowableSalary,
    } = minMaxAndAllowables;

    const translations = getValidationTranslations(
      t,
      minMaxAndAllowables,
      currency,
    );

    if (!validationType || validationType === 'min') {
      error = getErrorForMinValidationType(translations, {
        minimum,
        minimumText,
        minAllowableSalary,
        maxAllowableSalary,
      });
      setSalaryErrorMin(error.min);
      if (maxSalaryText.length) {
        error.max = deduceMaxSalaryTextError(
          translations.tValMaxGreaterThanMin,
          { minimum, maximum },
        );
        setSalaryErrorMax(error.max);
      }
    }

    if (!validationType || validationType === 'max') {
      error = getErrorForMaxValidationType(translations, {
        maximumText,
        maximum,
        minimum,
        minAllowableSalary,
        maxAllowableSalary,
      });
      setSalaryErrorMax(error.max);
    }
    error.minMax = getMinMaxError(translations.tValMinMaxErrorMsg, () =>
      minMaxErrorPredicate(
        error,
        { minimum, maximum, allowableMax, allowableMin },
        error.min,
        error.max,
      ),
    );
    setSalaryError(error.minMax);

    return error.minMax || error.min || error.max;
  };
